import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useMsal } from '@azure/msal-react';
import CheckIcon from 'mdi-react/CheckIcon';

import YesNoModal from 'modules/shared/YesNoModal';
import { useModal } from 'modules/shared/hooks';
import ModalAcept from 'modules/kiosk/components/ModalAcept';
// import { switchUserRole } from 'modules/auth/containers/Auth/actions';
import Loader from 'modules/kiosk/components/Loader';
import { BranchData } from '../../../entities/Branches/interfaces';
import { RoleValues, routes } from '../../../entities/Roles';
import { cleanTrip } from '../CommonTrip/actions';
import { setDepTitle } from '../Header/actions';
import { logoutSelector } from './selectors';
import { getAllBranchError, getAllBranchRequest, getRolesError, getRolesRequest, setReduxBranchTitle } from './actions';
import { ReactComponent as LogoutIcon } from './assets/logout.svg';
import { RootState } from '../../../rootReducer';
import ErrorMsg from '../../shared/ErrorMsg';

type Props = {
  classNameLogout?: string;
  classNameItem?: string;
};

const LogoutBtn: React.FC<Props> = ({ classNameLogout = '', classNameItem = '' }) => {
  const location = useLocation();

  const dispatch = useDispatch();
  const msal = useMsal();

  const {
    roles,
    branchTitle,
    branches,
    allRolesError,
    allBranchesError,
    loading: branchLoading,
  } = useSelector(logoutSelector);
  const { role, loading: roleLoading } = useSelector((state: RootState) => state.authModuleReducer.authReducer);
  const { isShow: isShowLogout, openModal: openModalLogout, closeModal: closeModalLogout } = useModal();
  const { isShow: isShowPopup, openModal: openPopup, closeModal: closePopup } = useModal();
  const [activeRoleSegment] = useMemo(() => location.pathname.slice(1).split('/'), [location]);
  const isSuperUser = role?.name && role?.name === RoleValues.ARM_SUPERUSER;
  const isManager = role?.name && role?.name === RoleValues.ARM_MANAGER;
  const isKiosk = activeRoleSegment === 'kiosk';

  const handleLogoutClick = () => {
    localStorage.clear();
    msal.instance.logoutRedirect();
  };

  const onDropdownBranches = (eventKey: any) => {
    const title = eventKey || {};

    localStorage.removeItem('/ordering');
    localStorage.removeItem('/security');
    dispatch(cleanTrip());
    dispatch(setReduxBranchTitle(title));
    // @ts-ignore
    dispatch(setDepTitle(null));
    localStorage.setItem('branchId', JSON.stringify(title));
    localStorage.removeItem('depTitle');
  };

  useEffect(() => {
    if (role && isSuperUser && branchTitle) dispatch(getRolesRequest(branchTitle.id));
  }, [branchTitle]);

  useEffect(() => {
    if ((isSuperUser || isManager) && !branches) dispatch(getAllBranchRequest());
  }, []);

  useEffect(() => {
    if (allRolesError || allBranchesError) {
      openPopup();
    }
  }, [allRolesError, allBranchesError]);

  return (
    <>
      {(branchLoading || roleLoading) && <Loader />}

      {isSuperUser ? (
        <DropdownButton
          menuAlign="right"
          variant="outline-secondary"
          className={classNames('ml-2 p-0', classNameLogout)}
          title={<LogoutIcon />}
        >
          <div className="d-flex flex-row">
            <div>
              <Dropdown.Header>Оберіть роль:</Dropdown.Header>
              {!!roles && roles.length
                ? roles.map(({ id, name, title }, index) => {
                    if (id === 5 || id === 8) return;
                    const isActive = routes[name] === `/${activeRoleSegment}`;
                    return (
                      <Link key={index} to={routes[name]} className={classNameItem}>
                        <Dropdown.Item
                          as="button"
                          onClick={() => {
                            localStorage.removeItem('/ordering');
                            localStorage.removeItem('/security');
                            dispatch(cleanTrip());
                          }}
                        >
                          {title} {isActive && <CheckIcon />}
                        </Dropdown.Item>
                      </Link>
                    );
                  })
                : null}
            </div>
            {!!branches && branches.length ? (
              <div>
                <Dropdown.Header>Оберіть філію:</Dropdown.Header>
                {branches
                  .filter((curBranch: BranchData) => curBranch.id !== 0 && !curBranch.isDeleted)
                  .map((branch, index: number) => {
                    if (branch.isDeleted) return;
                    const isActive = isEqual(branch.id, branchTitle?.id);
                    return (
                      <button
                        type="button"
                        key={index}
                        id={`${branch.id}`}
                        onClick={() => onDropdownBranches(branch)}
                        className={classNames(classNameItem)}
                      >
                        {branch.name} {isActive && <CheckIcon />}
                      </button>
                    );
                  })}
              </div>
            ) : null}
          </div>
          <Dropdown.Divider />
          <Link key={5} to={routes.ARM_ADMINISTRATOR} className={classNameItem}>
            <Dropdown.Item as="button" onClick={() => dispatch(cleanTrip())}>
              Адміністратор {routes.ARM_ADMINISTRATOR === `/${activeRoleSegment}` && <CheckIcon />}
            </Dropdown.Item>
          </Link>

          <Dropdown.Divider />
          <Dropdown.Item className="text-center" eventKey="6" onClick={openModalLogout}>
            Вийти
          </Dropdown.Item>
        </DropdownButton>
      ) : (
        <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-exit">Вийти та вилогінитись</Tooltip>}>
          <LogoutIcon className={classNames('ml-2 p-0', classNameLogout)} onClick={openModalLogout} />
        </OverlayTrigger>
      )}
      {isKiosk ? (
        <ModalAcept
          show={isShowLogout}
          onHide={closeModalLogout}
          onClickVerify={handleLogoutClick}
          textTitle="Вийти з системи"
        />
      ) : (
        <YesNoModal
          isShow={isShowLogout}
          title="Вийти з системи"
          acceptHandler={handleLogoutClick}
          closeModal={closeModalLogout}
        />
      )}
      <ErrorMsg
        isShow={isShowPopup}
        closeHandler={() => {
          closePopup();
          if (allRolesError) {
            dispatch(getRolesError(null));
          }
          if (allBranchesError) {
            dispatch(getAllBranchError(null));
          }
        }}
        // @ts-ignore
        error={allRolesError?.message || allBranchesError?.message || 'Виникла помилка'}
      />
    </>
  );
};

export default LogoutBtn;
